import { useEffect, useState } from 'react';

/**
 * useRecentEvent - I report whether something has happened lately, and go quiet on my own after a
 * while. ONLY concerned with the window - what the event was, and what being inside it should look
 * like, are the caller's.
 *
 * **Spark's new-talent announcement.** Answers arrive with more matching talent while the grid is
 * shut, and the badge and the drawer button both light up to say so, then settle - which is one
 * window, read in two places.
 */

/** How long an announcement lasts when the caller does not say. */
const DURATION_MS = 10_000;

/**
 * Two components each running this themselves each hold their own timer from their own render,
 * which is a way for one to still be lit while the other has gone dark.
 */
export const useRecentEvent = ({
  eventCount,
  durationMs = DURATION_MS,
}: {
  eventCount: number;
  /**
   * **One value for every consumer, not one each.** Several components can be announcing the same
   * event, and two clocks would let the halves of that announcement end at different moments - so
   * this belongs to whoever owns the event, called once and handed down, rather than to each of
   * them.
   */
  durationMs?: number;
}) => {
  const [isRecent, setIsRecent] = useState(eventCount > 0);

  /*
    A count rather than a boolean is what lets a second event restart the window: a flag that is
    already true never changes, so every arrival after the first would be silent.

    A timer rather than an `animationend` listener: under reduced motion `animation: none` means
    no `animationend` ever arrives and the flag would stay raised for good.
  */
  useEffect(() => {
    setIsRecent(eventCount > 0);

    if (eventCount === 0) return;

    const timer = setTimeout(() => {
      setIsRecent(false);
    }, durationMs);

    return () => {
      clearTimeout(timer);
    };
  }, [eventCount, durationMs]);

  return isRecent;
};
